/**
 * The frame every accounting form shares: title, close, an error line, and
 * Cancel / Save at the bottom. The forms only supply their fields.
 */

import PropTypes from "prop-types";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import Alert from "@mui/material/Alert";
import Icon from "@mui/material/Icon";

import SuiBox from "components/SuiBox";
import SuiTypography from "components/SuiTypography";

export { FIELD_SX } from "./Fields";

/**
 * A two-column grid that folds to one column on a phone. Rows are spaced
 * by the fields' own helper line, so only the columns need a gap.
 */
export function FieldGrid({ children, columns }) {
  return (
    <SuiBox
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "1fr", sm: `repeat(${columns}, 1fr)` },
        columnGap: 2,
        rowGap: 0.5,
        alignItems: "start",
      }}
    >
      {children}
    </SuiBox>
  );
}

FieldGrid.defaultProps = { columns: 2 };
FieldGrid.propTypes = { children: PropTypes.node.isRequired, columns: PropTypes.number };

function DialogShell({
  open,
  title,
  onClose,
  onSave,
  saving,
  error,
  saveLabel,
  maxWidth,
  children,
  extraActions,
}) {
  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : onClose}
      fullWidth
      maxWidth={maxWidth}
      PaperProps={{
        sx: ({ borders, boxShadows }) => ({
          borderRadius: borders.borderRadius.lg,
          boxShadow: boxShadows.xl,
        }),
      }}
    >
      <DialogTitle
        sx={({ palette, functions: { rgba } }) => ({
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          borderBottom: `1px solid ${rgba(palette.dark.main, 0.08)}`,
          pb: 1.5,
        })}
      >
        <SuiTypography variant="h5" fontWeight="bold">
          {title}
        </SuiTypography>
        <Icon
          onClick={saving ? undefined : onClose}
          sx={({ palette, functions: { rgba } }) => ({
            cursor: saving ? "default" : "pointer",
            fontSize: 26,
            color: rgba(palette.dark.main, 0.45),
            "&:hover": { color: palette.error.main },
          })}
        >
          close
        </Icon>
      </DialogTitle>

      <DialogContent sx={{ pt: "20px !important" }}>
        {error && (
          <SuiBox mb={2}>
            <Alert severity="error">{error}</Alert>
          </SuiBox>
        )}
        {children}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
        {extraActions}
        <SuiBox flex={1} />
        <Button variant="outlined" color="dark" onClick={onClose} disabled={saving}>
          {onSave ? "Cancel" : "Close"}
        </Button>
        {onSave && (
          <Button variant="contained" color="primary" onClick={onSave} disabled={saving}>
            {saving ? "Saving..." : saveLabel}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

DialogShell.defaultProps = {
  onSave: null,
  saving: false,
  error: "",
  saveLabel: "Save",
  maxWidth: "sm",
  extraActions: null,
};

DialogShell.propTypes = {
  open: PropTypes.bool.isRequired,
  title: PropTypes.node.isRequired,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func,
  saving: PropTypes.bool,
  error: PropTypes.string,
  saveLabel: PropTypes.string,
  maxWidth: PropTypes.string,
  children: PropTypes.node.isRequired,
  extraActions: PropTypes.node,
};

export default DialogShell;
